import React from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { Toaster } from './components/ui/sonner';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, ProtectedRoute } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { AdminAuthProvider, AdminProtectedRoute } from './context/AdminAuthContext';
import { AdminThemeProvider } from './context/AdminThemeContext';
import CartModal from './components/CartModal';
import CartNotification from './components/CartNotification';

// Customer Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import Dashboard from './pages/Dashboard';
import VenuesPage from './pages/VenuesPage';
import VenueDetailPage from './pages/VenueDetailPage';
import BookingPage from './pages/BookingPage';
import MyBookingsPage from './pages/MyBookingsPage';
import OpenPlayPage from './pages/OpenPlayPage';
import PlayersPage from './pages/PlayersPage';
import MembershipsPage from './pages/MembershipsPage';
import EquipmentPage from './pages/EquipmentPage';
import AddOnsPage from './pages/AddOnsPage';
import EventsPage from './pages/EventsPage';
import RewardsPage from './pages/RewardsPage';
import ProfilePage from './pages/ProfilePage';
import SupportPage from './pages/SupportPage';
import SettingsPage from './pages/SettingsPage';
import NotificationsPage from './pages/NotificationsPage';
import Rates from './pages/Rates';
import Location from './pages/Location';
import CourtRules from './pages/CourtRules';
import PaymentCallbackPage from './pages/PaymentCallbackPage';
import SuccessPaymentPage from './pages/SuccessPaymentPage';

// Admin Pages
import AdminLoginPage from './pages/admin/AdminLoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import AdminUsersPage from './pages/admin/AdminUsersPage';
import AdminBookingsPage from './pages/admin/AdminBookingsPage';
import AdminCourtsPage from './pages/admin/AdminCourtsPage';
import AdminPOSPage from './pages/admin/AdminPOSPage';
import AdminAddOnsPage from './pages/admin/AdminAddOnsPage';
import AdminEventsPage from './pages/admin/AdminEventsPage';
import AdminUsersManagePage from './pages/admin/AdminUsersManagePage';
import AdminSupportPage from './pages/admin/AdminSupportPage';
import AdminPaymentsPage from './pages/admin/AdminPaymentsPage';
import AdminOpenPlayPage from './pages/admin/AdminOpenPlayPage';
import AdminReviewsPage from './pages/admin/AdminReviewsPage';
import AdminRewardsPage from './pages/admin/AdminRewardsPage';
import AdminMembershipsPage from './pages/admin/AdminMembershipsPage';
import AdminEquipmentPage from './pages/admin/AdminEquipmentPage';
import AdminNotificationsPage from './pages/admin/AdminNotificationsPage';
import AdminFAQsPage from './pages/admin/AdminFAQsPage';
import AdminVenuePage from './pages/admin/AdminVenuePage';
import AdminGalleryPage from './pages/admin/AdminGalleryPage';
import AdminTransactionsPage from './pages/admin/AdminTransactionsPage';
import AdminSuccessPaymentPage from './pages/admin/AdminSuccessPaymentPage';
import AdminSpecialPackagesPage from './pages/admin/AdminSpecialPackagesPage';
import AdminPOSBookingPage from './pages/admin/AdminPOSBookingPage';
import AdminKioskBookingsPage from './pages/admin/AdminKioskBookingsPage';
import AdminEmbedPage from './pages/admin/AdminEmbedPage';
import AdminLogsPage from './pages/admin/AdminLogsPage';
import AdminBookingHistoryPage from './pages/admin/AdminBookingHistoryPage';
import AdminWebLog from './pages/admin/AdminWebLog';

import './App.css';

const GOOGLE_CLIENT_ID = '368879857036-48hri1ctil5cs1kfkucfq805vkkfmc4a.apps.googleusercontent.com';

function CustomerRouter() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/rates" element={<Rates />} />
      <Route path="/location" element={<Location />} />
      <Route path="/court-rules" element={<CourtRules />} />
      
      {/* Protected Routes */}
      <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
      <Route path="/venues" element={<ProtectedRoute><VenuesPage /></ProtectedRoute>} />
      <Route path="/venues/:venueId" element={<ProtectedRoute><VenueDetailPage /></ProtectedRoute>} />
      <Route path="/book/:venueId" element={<ProtectedRoute><BookingPage /></ProtectedRoute>} />
      <Route path="/payment/callback" element={<ProtectedRoute><PaymentCallbackPage /></ProtectedRoute>} />
      <Route path="/my-bookings" element={<ProtectedRoute><MyBookingsPage /></ProtectedRoute>} />
      <Route path="/open-play" element={<ProtectedRoute><OpenPlayPage /></ProtectedRoute>} />
      <Route path="/players" element={<ProtectedRoute><PlayersPage /></ProtectedRoute>} />
      <Route path="/memberships" element={<ProtectedRoute><MembershipsPage /></ProtectedRoute>} />
      <Route path="/equipment" element={<ProtectedRoute><EquipmentPage /></ProtectedRoute>} />
      <Route path="/add-ons" element={<ProtectedRoute><AddOnsPage /></ProtectedRoute>} />
      <Route path="/events" element={<ProtectedRoute><EventsPage /></ProtectedRoute>} />
      {/* <Route path="/rewards" element={<ProtectedRoute><RewardsPage /></ProtectedRoute>} /> */}
      <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
      <Route path="/support" element={<ProtectedRoute><SupportPage /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
      <Route path="/notifications" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
      <Route path="/success-payment" element={<ProtectedRoute><SuccessPaymentPage /></ProtectedRoute>} />
    </Routes>
  );
}

function AdminRouter() {
  return (
    <Routes>
      {/* Admin Public Route */}
      <Route path="/admin/login" element={<AdminLoginPage />} />
      
      {/* Admin Protected Routes */}
      <Route path="/admin/dashboard" element={<AdminProtectedRoute><AdminDashboard /></AdminProtectedRoute>} />
      <Route path="/admin/users" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminUsersPage /></AdminProtectedRoute>} />
      <Route path="/admin/bookings" element={<AdminProtectedRoute><AdminBookingsPage /></AdminProtectedRoute>} />
      <Route path="/admin/special-packages" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminSpecialPackagesPage /></AdminProtectedRoute>} />
      <Route path="/admin/courts" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminCourtsPage /></AdminProtectedRoute>} />
      <Route path="/admin/pos" element={<AdminProtectedRoute><AdminPOSPage /></AdminProtectedRoute>} />
      <Route path="/admin/transactions" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminTransactionsPage /></AdminProtectedRoute>} />
      <Route path="/admin/booking-history" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminBookingHistoryPage /></AdminProtectedRoute>} />
      <Route path="/admin/addons" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminAddOnsPage /></AdminProtectedRoute>} />
      <Route path="/admin/events" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminEventsPage /></AdminProtectedRoute>} />
      <Route path="/admin/admins" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminUsersManagePage /></AdminProtectedRoute>} />
      <Route path="/admin/support" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminSupportPage /></AdminProtectedRoute>} />
      <Route path="/admin/payments" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminPaymentsPage /></AdminProtectedRoute>} />
      <Route path="/admin/open-play" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor']}><AdminOpenPlayPage /></AdminProtectedRoute>} />
      <Route path="/admin/reviews" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminReviewsPage /></AdminProtectedRoute>} />
      <Route path="/admin/rewards" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminRewardsPage /></AdminProtectedRoute>} />
      <Route path="/admin/memberships" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminMembershipsPage /></AdminProtectedRoute>} />
      <Route path="/admin/equipment" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminEquipmentPage /></AdminProtectedRoute>} />
      <Route path="/admin/gallery" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminGalleryPage /></AdminProtectedRoute>} />
      <Route path="/admin/notifications" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminNotificationsPage /></AdminProtectedRoute>} />
      <Route path="/admin/faqs" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminFAQsPage /></AdminProtectedRoute>} />
      <Route path="/admin/venue" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminVenuePage /></AdminProtectedRoute>} />
      <Route path="/admin/embed" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager']}><AdminEmbedPage /></AdminProtectedRoute>} />
      <Route path="/admin/payment-successful" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminSuccessPaymentPage /></AdminProtectedRoute>} />
      <Route path="/admin/pos-bookings" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminPOSBookingPage /></AdminProtectedRoute>} />
      <Route path="/admin/kiosk-bookings" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminKioskBookingsPage /></AdminProtectedRoute>} />
      <Route path="/admin/web-log" element={<AdminProtectedRoute allowedRoles={['super_admin', 'manager', 'supervisor', 'cashier']}><AdminWebLog /></AdminProtectedRoute>} />
      <Route path="/admin/logs" element={<AdminProtectedRoute allowedRoles={['super_admin']}><AdminLogsPage /></AdminProtectedRoute>} />
    </Routes>
  );
}

function AppRouter() {
  const location = useLocation();
  
  // Route to admin section
  if (location.pathname.startsWith('/admin')) {
    return (
      <AdminAuthProvider>
        <AdminThemeProvider>
          <AdminRouter />
        </AdminThemeProvider>
      </AdminAuthProvider>
    );
  }
  
  // Route to customer section
  return (
    <AuthProvider>
      <CartProvider>
        <CustomerRouter />
        <CartModal />
        <CartNotification />
      </CartProvider>
    </AuthProvider>
  );
}

function App() {
  return (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <BrowserRouter>
        <ThemeProvider>
          <AppRouter />
          <Toaster position="top-right" richColors />
        </ThemeProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
